import { cn } from '@/lib/utils';

interface ScrollPageProps {
  /** Title and filters. Scrolls with the page. */
  header: React.ReactNode;
  /** Pagination or action bar. Sticks to the bottom of the scrolling area while content is taller than the screen. */
  footer?: React.ReactNode;
  /** Separate the footer from the content with a rule (for action bars) */
  footerBordered?: boolean;
  /** Accessible name of the content region */
  label?: string;
  /** Draw the bordered card around the content. Turn off for loading and empty states. */
  framed?: boolean;
  children: React.ReactNode;
}

/**
 * Page frame for list and form screens. Scrolling belongs to `<Layout>`'s main area, so this only arranges
 * header, content and a sticky footer. The first table inside keeps its column headings pinned while the page scrolls.
 */
export function ScrollPage({ header, footer, footerBordered = false, label, framed = true, children }: ScrollPageProps) {
  return (
    <>
      {header}
      <div
        role={label ? 'region' : undefined}
        aria-label={label}
        className={cn(
          framed && 'rounded-lg border bg-card',
          // Let the page scroll, not the table, so sticky headings work. On phones the table keeps its own horizontal scroll.
          'md:[&_[data-table-wrapper]]:overflow-visible',
          '[&>[data-table-wrapper]>table>thead>tr>th]:sticky [&>[data-table-wrapper]>table>thead>tr>th]:top-0 [&>[data-table-wrapper]>table>thead>tr>th]:z-10',
          '[&>[data-table-wrapper]>table>thead>tr>th]:bg-muted [&>[data-table-wrapper]>table>thead>tr>th]:shadow-[inset_0_-1px_0_hsl(var(--border))]'
        )}
      >
        {children}
      </div>
      {footer && (
        <div className={cn('sticky bottom-0 z-20 -mb-6 bg-background py-3 sm:-mb-8', footerBordered && 'border-t')}>{footer}</div>
      )}
    </>
  );
}
